import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsStringLiteral } from 'nuqs';
import type { inferParserType } from 'nuqs';
import { useStore } from '@nanostores/react';
import { servicesQuery } from '@/services/api';
import { isAPIError } from '@/services/api/ts-rest';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { authStore } from '@/services/auth/store';
import { useRoleDefinitions } from '@/features/roles/hook';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { isEmptyString } from '@/utils/string';
import { validateFiltersForNonPremium } from '../components/audit-logs-table/filter-utils';
import { getAuditLogDayLimit } from '../utils/access';
import type { AuditLogFilters, AuditLogResponse, AuditLogUser } from '../types';
import { AUDIT_LOGS_PAGE_SIZE } from '../constants/pagination';

/**
 * Check if an error is a retryable server error (500-599 status codes).
 * These errors are often transient and may succeed on retry.
 */
function isRetryableServerError(error: unknown): boolean {
  if (isAPIError(error)) {
    const status = error.response.status;
    // Retry on 5xx server errors (500-599)
    return status >= 500 && status < 600;
  }
  return false;
}

interface UseAuditLogsDataOptions {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
}

const filtersParsers = {
  user_id: parseAsInteger,
  ip_addr: parseAsString,
  object_type: parseAsString,
  ui_object_type: parseAsString, // UI-only: preserves APITriggeredBroadcast selection; campaign_type is derived from this
  object_id: parseAsString,
  attribute_name: parseAsString,
  action: parseAsString,
  start_time: parseAsString,
  end_time: parseAsString,
  sort_order: parseAsStringLiteral(['asc', 'desc']).withDefault('desc'),
  campaign_id: parseAsInteger,
};

type UrlFilters = inferParserType<typeof filtersParsers>;

export function useAuditLogsData({ context, contextId }: UseAuditLogsDataOptions) {
  const auth = useAuth();
  const authData = useStore(authStore);
  const { track } = useAnalytics();

  const [urlFilters, setUrlFilters] = useQueryStates(filtersParsers, {
    // Use 'replace' to avoid polluting browser history with every filter change
    history: 'replace',
  });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const filters = useMemo(() => {
    const updatedFilters: Partial<AuditLogFilters> = {};

    // Map `null` (used by nuqs for empty values) to `undefined` (used by our code)
    for (const [key, value] of Object.entries(urlFilters)) {
      const filterKey = key as keyof AuditLogFilters;
      (updatedFilters as Partial<AuditLogFilters>)[filterKey] = (value ?? undefined) as never;
    }

    return {
      ...updatedFilters,
      cursor,
      limit: AUDIT_LOGS_PAGE_SIZE,
    } as AuditLogFilters;
  }, [urlFilters, cursor]);

  const setFilters = useCallback(
    (newFilters: AuditLogFilters) => {
      const { limit: _limit, cursor, ...rest } = newFilters;
      const updatedRest: Partial<UrlFilters> = {};

      // Map `undefined` (used by our code) to `null` values (used by nuqs for empty values)
      for (const [key, value] of Object.entries(rest)) {
        const filterKey = key as keyof UrlFilters;
        (updatedRest as Partial<UrlFilters>)[filterKey] = (value ?? null) as never;
      }

      setUrlFilters(updatedRest);
      setCursor(cursor);
    },
    [setUrlFilters, setCursor],
  );

  const accountId = auth.account.id;
  const dayLimit = getAuditLogDayLimit(auth.account);

  // Get workspaces from auth store (available at account level for workspace filtering)
  const workspaces = useMemo(() => {
    return (
      authData.workspaces?.map((w) => ({
        id: Number.parseInt(w.id, 10),
        name: w.name,
      })) ?? []
    );
  }, [authData.workspaces]);

  // Check window.location directly on mount to avoid race condition with searchParams
  // This determines if we're in deep-link mode BEFORE React hydration affects searchParams
  // Use useState with lazy initializer to capture value once, avoiding side effects in useMemo
  const [initialAuditLogId] = useState<number | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('audit_log_id');
    if (!isEmptyString(idParam) && /^\d+$/.test(idParam)) {
      const parsed = Number.parseInt(idParam, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  });

  const [accumulatedLogs, setAccumulatedLogs] = useState<AuditLogResponse | undefined>(undefined);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [shouldLoadUsers, setShouldLoadUsers] = useState(false);
  const [hasUsedInitialAuditLogId, setHasUsedInitialAuditLogId] = useState(false);

  // Search state for paginated filter dropdowns
  const [campaignSearchString, setCampaignSearchString] = useState<string>('');
  const [newsletterSearchString, setNewsletterSearchString] = useState<string>('');
  const [transactionalMessageSearchString, setTransactionalMessageSearchString] = useState<string>('');

  // Get audit log ID from URL for deep linking
  // Use initialAuditLogId on first render to avoid race condition, then sync with searchParams
  // Mark consumption via state update in useEffect to avoid side effects in useMemo
  const [auditLogIdParam] = useQueryState('audit_log_id', parseAsString);
  const auditLogId = useMemo(() => {
    // On first render, use the initial value captured from window.location
    // This prevents the race condition where searchParams isn't populated yet
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null) {
      return initialAuditLogId;
    }

    // After first use, rely on searchParams which should be synced by now
    if (!isEmptyString(auditLogIdParam) && /^\d+$/.test(auditLogIdParam)) {
      const parsed = Number.parseInt(auditLogIdParam, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  }, [auditLogIdParam, initialAuditLogId, hasUsedInitialAuditLogId]);

  // Mark initial value as used after first consumption (moved out of useMemo to avoid side effects)
  useEffect(() => {
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null && auditLogId === initialAuditLogId) {
      setHasUsedInitialAuditLogId(true);
    }
  }, [hasUsedInitialAuditLogId, initialAuditLogId, auditLogId]);

  // Only create queries if we have the required data
  const shouldCreateQuery = contextId != null && auditLogId == null;
  const shouldFetchLogById = contextId != null && auditLogId != null;

  // API version for pre-computed diffs - include in cache keys to ensure proper invalidation
  const API_VERSION = 2;

  // Validate filters before API calls to ensure accounts with limited access can't bypass day limit
  // This is a safety check even though filters should already be validated
  // Also derive object_type and campaign_type from ui_object_type for backend filtering
  const validatedFilters = useMemo(() => {
    const validated = validateFiltersForNonPremium(filters, dayLimit);

    // Derive object_type and campaign_type from ui_object_type
    // This simplifies the URL - only ui_object_type is needed to distinguish Campaign vs APITriggeredBroadcast
    const uiObjectType = urlFilters.ui_object_type;
    if (uiObjectType === 'APITriggeredBroadcast') {
      // Ensure object_type is Campaign (in case URL only has ui_object_type)
      // Only set campaign_type when object_type is actually Campaign (required by backend constraint)
      if (validated.object_type == null || validated.object_type === 'Campaign') {
        validated.object_type = 'Campaign';
        validated.campaign_type = 'triggered_broadcast';
      }
    } else if (uiObjectType === 'Campaign') {
      // Only set campaign_type when object_type is actually Campaign (required by backend constraint)
      if (validated.object_type == null || validated.object_type === 'Campaign') {
        validated.object_type = 'Campaign';
        validated.campaign_type = 'exclude_triggered_broadcast';
      }
    }

    return validated;
  }, [filters, dayLimit, urlFilters.ui_object_type]);

  // Fetch audit logs based on context (using v2 API for pre-computed diffs)
  // Retry logic: Automatically retry on 500 errors (server errors) with exponential backoff
  const accountAuditLogsQuery = servicesQuery.getAccountAuditLogs.useQuery({
    queryKey: ['accountAuditLogs', contextId, validatedFilters, API_VERSION],
    queryData: {
      params: { account_id: contextId ?? 0 },
      query: { ...validatedFilters, version: API_VERSION },
    },
    enabled: context === 'account' && shouldCreateQuery,
    retry: (failureCount, error) => {
      // Retry up to 3 times on 500 errors (transient server errors)
      // failureCount starts at 1 after first failure, so < 4 allows retries at 1, 2, 3 (3 retries total)
      if (isRetryableServerError(error) && failureCount < 4) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s (max 30s)
  });

  const workspaceAuditLogsQuery = servicesQuery.getWorkspaceAuditLogs.useQuery({
    queryKey: ['workspaceAuditLogs', contextId, validatedFilters, API_VERSION],
    queryData: {
      params: { environment_id: contextId ?? 0 },
      query: { ...validatedFilters, version: API_VERSION },
    },
    enabled: context === 'workspace' && shouldCreateQuery,
    retry: (failureCount, error) => {
      // Retry up to 3 times on 500 errors (transient server errors)
      // failureCount starts at 1 after first failure, so < 4 allows retries at 1, 2, 3 (3 retries total)
      if (isRetryableServerError(error) && failureCount < 4) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s (max 30s)
  });

  // Deep link queries (using v2 API for pre-computed diffs)
  const accountAuditLogByIdQuery = servicesQuery.getAccountAuditLogById.useQuery({
    queryKey: ['accountAuditLogById', contextId, auditLogId, API_VERSION],
    queryData: {
      params: { account_id: contextId ?? 0, id: auditLogId ?? 0 },
      query: { version: API_VERSION },
    },
    enabled: context === 'account' && shouldFetchLogById,
  });

  const workspaceAuditLogByIdQuery = servicesQuery.getWorkspaceAuditLogById.useQuery({
    queryKey: ['workspaceAuditLogById', contextId, auditLogId, API_VERSION],
    queryData: {
      params: { environment_id: contextId ?? 0, id: auditLogId ?? 0 },
      query: { version: API_VERSION },
    },
    enabled: context === 'workspace' && shouldFetchLogById,
  });

  // Fetch all users for the account to populate User dropdown filter (lazy loaded)
  const allUsersQuery = servicesQuery.getAllUsers.useQuery({
    queryKey: ['allUsers', accountId],
    queryData: {
      params: { account_id: accountId ?? 0 },
    },
    enabled: shouldLoadUsers && accountId != null,
  });

  // Note: segments are eagerly loaded since they're needed to display names in activity logs and expanded details
  // Campaigns, newsletters, and transactional messages now use deliveries/filters endpoint for full lists
  // This matches the UI codebase approach and allows client-side filtering

  // Fetch segments for the workspace (eagerly loaded since they're needed for displaying
  // segment names in conditions, triggers, and filters within expanded details)
  const segmentsQuery = servicesQuery.getSegments.useQuery({
    queryKey: ['segments', contextId],
    queryData: {
      params: { environment_id: contextId ?? 0 },
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Fetch delivery filters (messages, campaigns, newsletters, transactional messages) for the workspace
  // This endpoint returns full lists without pagination, matching the UI codebase approach
  // We use client-side filtering instead of server-side search
  const deliveryFiltersQuery = servicesQuery.getDeliveryFilters.useQuery({
    queryKey: ['deliveryFilters', contextId],
    queryData: {
      params: { environment_id: contextId ?? 0 },
      query: {},
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Transform delivery filters response to extract campaigns, newsletters, transactional messages, and messages
  // This endpoint returns full lists, allowing client-side filtering (like UI codebase)
  const deliveryFiltersData = useMemo(() => {
    // Handle errors gracefully - if the endpoint doesn't exist yet, return empty arrays
    if (deliveryFiltersQuery.error != null || deliveryFiltersQuery.data == null) {
      return {
        campaigns: [],
        newsletters: [],
        transactionalMessages: [],
        messages: [],
      };
    }

    const filtersData = deliveryFiltersQuery.data.body;
    if (filtersData == null || typeof filtersData !== 'object') {
      return {
        campaigns: [],
        newsletters: [],
        transactionalMessages: [],
        messages: [],
      };
    }

    // Extract campaigns (convert id from string to number to match expected format)
    const campaignsList = filtersData.campaigns ?? [];
    const campaigns =
      Array.isArray(campaignsList) && campaignsList.length >= 0
        ? campaignsList.map((campaign) => {
            // Handle type as either string or number (API might return numeric types)
            let typeValue: string | number | undefined = campaign.type;
            if (typeValue == null) {
              typeValue = undefined;
            } else if (typeof typeValue !== 'number') {
              // Convert to string if not a number
              typeValue = String(typeValue);
            }
            // If it's a number, keep as is for lookup in CampaignTypeLabels
            return {
              id: Number.parseInt(String(campaign.id ?? '0'), 10),
              name: String(campaign.name ?? ''),
              type: typeValue,
            };
          })
        : [];

    // Extract newsletters (convert id from string to number)
    const newslettersList = filtersData.newsletters ?? [];
    const newsletters =
      Array.isArray(newslettersList) && newslettersList.length >= 0
        ? newslettersList.map((newsletter) => ({
            id: Number.parseInt(String(newsletter.id ?? '0'), 10),
            name: String(newsletter.name ?? ''),
          }))
        : [];

    // Extract transactional messages (convert id from string to number)
    const transactionalMessagesList = filtersData.transactional_messages ?? [];
    const transactionalMessages =
      Array.isArray(transactionalMessagesList) && transactionalMessagesList.length >= 0
        ? transactionalMessagesList.map((tm) => ({
            id: Number.parseInt(String(tm.id ?? '0'), 10),
            name: String(tm.name ?? ''),
          }))
        : [];

    // Extract messages (use segmentable messages or fall back to all messages)
    const messagesList = filtersData.segmentable ?? filtersData.messages ?? [];
    const messages =
      Array.isArray(messagesList) && messagesList.length >= 0
        ? messagesList
            .map((msg) => {
              if (msg == null || typeof msg !== 'object') {
                return null;
              }
              // Cast to access optional name field not in type definition
              const msgWithName = msg as typeof msg & { name?: string };
              return {
                output_id: String(msg.output_id ?? ''),
                subject: msg.subject as string | undefined,
                name: (msg.subject as string | undefined) ?? msgWithName.name,
                type: msg.type as string | undefined,
                campaign_id:
                  msg.campaign_id != null && msg.campaign_id !== ''
                    ? Number.parseInt(String(msg.campaign_id), 10)
                    : undefined,
                transactional_message_id:
                  msg.transactional_message_id != null && msg.transactional_message_id !== ''
                    ? String(msg.transactional_message_id)
                    : undefined,
              };
            })
            .filter((msg): msg is NonNullable<typeof msg> => msg != null)
        : [];

    return {
      campaigns,
      newsletters,
      transactionalMessages,
      messages,
    };
  }, [deliveryFiltersQuery.data, deliveryFiltersQuery.error]);

  // Fetch object types for the workspace (eagerly loaded for relationship conditions)
  const objectTypesQuery = useListWorkspaceObjectTypes(context === 'workspace' ? contextId ?? undefined : undefined);

  const auditLogsQuery = context === 'account' ? accountAuditLogsQuery : workspaceAuditLogsQuery;
  const singleLogQuery = context === 'account' ? accountAuditLogByIdQuery : workspaceAuditLogByIdQuery;

  // Restore accumulated logs from sessionStorage on mount
  useEffect(() => {
    if (!hasRestoredFromStorage && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        const stored = sessionStorage.getItem(key);
        if (!isEmptyString(stored)) {
          const parsedData = JSON.parse(stored);
          setAccumulatedLogs(parsedData);
        }
      } catch (_error) {
        // Silently fail if restoration fails
      }
      setHasRestoredFromStorage(true);
      setIsInitialMount(false);
    }
  }, [contextId, hasRestoredFromStorage, context]);

  // Accumulate logs when "Load More" is clicked
  useEffect(() => {
    if (auditLogsQuery?.data != null) {
      const newData = auditLogsQuery.data.body;

      if (!isEmptyString(filters.cursor)) {
        setAccumulatedLogs((prev) => {
          if (prev == null) return newData;

          // Deduplicate audit logs
          const logsMap = new Map<number, (typeof newData.data)[0]>();
          for (const log of [...(prev.data ?? []), ...(newData.data ?? [])]) {
            logsMap.set(log.id, log as (typeof newData.data)[0]);
          }

          // Deduplicate users
          const usersMap = new Map<number, AuditLogUser>();
          for (const user of [...(prev.users ?? []), ...(newData.users ?? [])]) {
            usersMap.set(user.user_id, user);
          }

          return {
            data: Array.from(logsMap.values()),
            users: Array.from(usersMap.values()),
            pagination: newData.pagination,
          };
        });
      } else {
        setAccumulatedLogs(newData);
      }
    }
  }, [auditLogsQuery.data, filters.cursor]);

  // Save accumulated logs to sessionStorage
  useEffect(() => {
    if (accumulatedLogs != null && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        sessionStorage.setItem(key, JSON.stringify(accumulatedLogs));
      } catch (_error) {
        // Ignore storage errors
      }
    }
  }, [accumulatedLogs, contextId, context]);

  // Reset accumulated logs when filters change
  useEffect(() => {
    if (isInitialMount) {
      return;
    }

    // Only reset if we're not paginating
    if (isEmptyString(filters.cursor)) {
      setAccumulatedLogs(undefined);
      setHasRestoredFromStorage(false);
      if (contextId != null) {
        try {
          const key = `audit-logs-${context}-${contextId}`;
          sessionStorage.removeItem(key);
        } catch (_error) {
          // Ignore errors
        }
      }
    }
  }, [
    filters.user_id,
    filters.ip_addr,
    filters.object_type,
    filters.object_id,
    filters.attribute_name,
    filters.action,
    filters.campaign_id,
    filters.start_time,
    filters.end_time,
    filters.sort_order,
    filters.cursor,
    isInitialMount,
    contextId,
    context,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<AuditLogFilters>) => {
      // If only the cursor is being set (Load More), don't reset it
      // Otherwise, reset cursor when filter values change
      if (Object.keys(newFilters).length === 1 && 'cursor' in newFilters) {
        setFilters(newFilters as AuditLogFilters);
      } else {
        setFilters({ ...newFilters, cursor: undefined } as AuditLogFilters);
      }

      // Handle ui_object_type separately - preserve UI selection for APITriggeredBroadcast
      // When object_type is Campaign, we need to check if it came from APITriggeredBroadcast
      // We'll check the current filter values to see if user selected APITriggeredBroadcast
      // For now, we'll handle this by checking if ui_object_type should be set
      // This will be set when the user selects APITriggeredBroadcast in the UI

      track('auditLogs', 'filtersChanged', {
        filterCount: Object.keys(newFilters).length,
      });
    },
    [setFilters, track],
  );

  const handleLoadUsers = useCallback(() => {
    setShouldLoadUsers(true);
  }, []);

  const handleLoadSegments = useCallback(() => {
    // Segments are eagerly loaded, so this is a no-op but kept for API consistency
  }, []);

  const handleSetUiObjectType = useCallback(
    (uiObjectType: string | null) => {
      setUrlFilters({ ui_object_type: uiObjectType });
    },
    [setUrlFilters],
  );

  // Handlers for search (client-side filtering, no API calls needed)
  // Search state is managed in table-filters.tsx for client-side filtering
  const handleCampaignSearch = useCallback((searchString: string) => {
    setCampaignSearchString(searchString);
  }, []);

  const handleNewsletterSearch = useCallback((searchString: string) => {
    setNewsletterSearchString(searchString);
  }, []);

  const handleTransactionalMessageSearch = useCallback((searchString: string) => {
    setTransactionalMessageSearchString(searchString);
  }, []);

  // No-op handlers for load more (not needed with deliveries/filters endpoint)
  const handleLoadMoreCampaigns = useCallback(() => {
    // No-op: deliveries/filters returns full list, no pagination needed
  }, []);

  const handleLoadMoreNewsletters = useCallback(() => {
    // No-op: deliveries/filters returns full list, no pagination needed
  }, []);

  // Fetch role definitions for mapping role IDs to friendly names
  const roleDefinitions = useRoleDefinitions();

  // Extract data from delivery filters (already flattened, no pagination needed)
  const allCampaigns = deliveryFiltersData.campaigns;
  const allNewsletters = deliveryFiltersData.newsletters;
  const allTransactionalMessages = deliveryFiltersData.transactionalMessages;
  const messages = deliveryFiltersData.messages;

  // Extract segments from segmentsQuery response
  const segments = useMemo(() => {
    if (segmentsQuery.data == null || segmentsQuery.error != null) {
      return [];
    }
    const segmentsData = segmentsQuery.data.body;
    if (segmentsData == null) {
      return [];
    }

    // Handle different response structures:
    // 1. { segments: ServicesApiSegment[] } - standard structure
    // 2. ServicesApiSegment[] - direct array (fallback)
    let segmentsList: unknown[] = [];
    if (Array.isArray(segmentsData) && segmentsData.length >= 0) {
      // Direct array response
      segmentsList = segmentsData;
    } else if (typeof segmentsData === 'object' && segmentsData != null && 'segments' in segmentsData) {
      // Object with segments property
      const segmentsProp = (segmentsData as { segments?: unknown }).segments;
      if (Array.isArray(segmentsProp) && segmentsProp.length >= 0) {
        segmentsList = segmentsProp;
      }
    }

    return segmentsList
      .map((segment) => {
        if (segment == null || typeof segment !== 'object') {
          return null;
        }
        const seg = segment as { id?: unknown; name?: unknown };
        const id = seg.id != null ? Number.parseInt(String(seg.id), 10) : Number.NaN;
        const name = seg.name != null ? String(seg.name) : '';
        if (Number.isNaN(id)) {
          return null;
        }
        return { id, name };
      })
      .filter((seg): seg is { id: number; name: string } => seg != null);
  }, [segmentsQuery.data, segmentsQuery.error]);

  return {
    filters,
    uiObjectType: urlFilters.ui_object_type, // UI-only parameter to preserve APITriggeredBroadcast selection
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    segmentsQuery,
    deliveryFiltersQuery,
    // Aliases for backward compatibility with tests
    campaignsQuery: deliveryFiltersQuery,
    newslettersQuery: deliveryFiltersQuery,
    transactionalMessagesQuery: deliveryFiltersQuery,
    objectTypesQuery,
    workspaces,
    handleFiltersChange,
    handleSetUiObjectType,
    handleLoadUsers,
    handleLoadSegments,
    // Segments (eagerly loaded for displaying names in activity logs)
    segments,
    isLoadingSegments: segmentsQuery.isLoading,
    // Campaign handlers (using deliveries/filters endpoint, client-side filtering)
    campaigns: allCampaigns,
    isLoadingCampaigns: deliveryFiltersQuery.isLoading,
    isFetchingMoreCampaigns: false,
    hasMoreCampaigns: false,
    onLoadMoreCampaigns: handleLoadMoreCampaigns,
    onCampaignSearch: handleCampaignSearch,
    campaignSearchString,
    // Newsletter handlers (using deliveries/filters endpoint, client-side filtering)
    newsletters: allNewsletters,
    isLoadingNewsletters: deliveryFiltersQuery.isLoading,
    isFetchingMoreNewsletters: false,
    hasMoreNewsletters: false,
    onLoadMoreNewsletters: handleLoadMoreNewsletters,
    onNewsletterSearch: handleNewsletterSearch,
    newsletterSearchString,
    // Transactional message handlers (using deliveries/filters endpoint, client-side filtering)
    transactionalMessages: allTransactionalMessages,
    isLoadingTransactionalMessages: deliveryFiltersQuery.isLoading,
    onTransactionalMessageSearch: handleTransactionalMessageSearch,
    transactionalMessageSearchString,
    // Legacy props for backward compatibility
    isLoadingUsers: allUsersQuery.isLoading,
    messages,
    isLoadingMessages: deliveryFiltersQuery.isLoading,
    onLoadMessages: () => {
      // Messages are eagerly loaded, so this is a no-op but kept for API consistency
    },
    roleDefinitions,
  };
}
